// Link management related sagas

import { put, select } from 'redux-saga/effects';

// Services
import { ErrorCategory, handleError } from '@services/errorService';

// Api
import client from '@api';

// Actions
import { UpdatedMarbleUser } from '@actions/user';
import { SetLoading } from '@actions/loading';
import { SetRedirectControl } from '@actions/redirectControl';

// Graph
import { createLink, deleteLink, updateLink } from '@graphql/mutations/link';

// Utils
import modifyLinkObject from '@utils/modifyLinkObject';

// Local utils
import { uploadImageSaga } from './utils';

// Types
import type { CreateLinkPayload, Link, PayloadAction } from './types';

export function* UpdateLinkSaga({ payload }: PayloadAction<Link>): Generator {
  try {
    yield put(SetLoading(true));
    const { userID, link, isOnboardingPassed, username } = (yield select(
      (state: {
        User: {
          _id: string;
          links: Link[];
          onboardingPassed: boolean;
          username: string;
        };
      }) => ({
        userID: state.User._id,
        link: state.User.links.find(({ _id }) => _id === payload._id),
        isOnboardingPassed: !!state.User.onboardingPassed,
        username: state.User?.username,
      }),
    )) as {
      userID: string;
      link: Link;
      isOnboardingPassed: boolean;
      username: string;
    };

    let imageUrl: string | undefined;

    if (payload?.imageUrl?.blob) {
      imageUrl = (yield uploadImageSaga(payload?.imageUrl?.blob)) as
        | string
        | undefined;
    }

    const { data } = (yield client().mutate({
      mutation: updateLink,
      variables: {
        input: [
          {
            ...(imageUrl
              ? {
                  ...modifyLinkObject(link),
                  ...modifyLinkObject(payload),
                  imageUrl,
                }
              : payload?.imageUrl?.blob
              ? {
                  ...modifyLinkObject(link),
                  ...modifyLinkObject(payload),
                  imageUrl: null,
                }
              : { ...modifyLinkObject(link), ...modifyLinkObject(payload) }),
          },
        ],
      },
    })) as { data: { updateLink: Link[] } };

    yield put(
      UpdatedMarbleUser({
        links: data?.updateLink,
      }),
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      userID,
      event: 'linkUpdated',
    });

    if (isOnboardingPassed) {
      yield put(SetRedirectControl(`/${username}`));
    } else {
      if (location.pathname.includes('/onboarding/links')) {
        localStorage.setItem('onboardingPath', '/onboarding/profile');
        yield put(SetRedirectControl('/onboarding/profile'));
      } else {
        localStorage.setItem('onboardingPath', '/onboarding/link');
        yield put(SetRedirectControl('/onboarding/link'));
      }
    }

    yield put(SetLoading(false));
  } catch (error) {
    handleError(error, { category: ErrorCategory.API });
    yield put(SetLoading(false));
  }
}

export function* CreateLinkSaga({
  payload,
}: PayloadAction<CreateLinkPayload>): Generator {
  try {
    yield put(SetLoading(true));
    const { userID, isOnboardingPassed, username } = (yield select(
      (state: {
        User: { _id: string; onboardingPassed: boolean; username: string };
      }) => ({
        userID: state.User._id,
        isOnboardingPassed: !!state.User.onboardingPassed,
        username: state.User?.username,
      }),
    )) as {
      userID: string;
      isOnboardingPassed: boolean;
      username: string;
    };

    let imageUrl: string | undefined;

    if (payload?.imageUrl?.blob) {
      imageUrl = (yield uploadImageSaga(payload?.imageUrl?.blob)) as
        | string
        | undefined;
    }

    const { data } = (yield client().mutate({
      mutation: createLink,
      variables: {
        input: [
          {
            ...(imageUrl
              ? { ...payload, imageUrl }
              : payload?.imageUrl?.blob
              ? { ...payload, imageUrl: null }
              : payload),
          },
        ],
      },
    })) as { data: { createLink: Link[] } };

    yield put(
      UpdatedMarbleUser({
        links: data?.createLink,
      }),
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      userID,
      event: 'linkCreated',
    });

    yield put(SetLoading(false));

    if (isOnboardingPassed) {
      yield put(SetRedirectControl(`/${username}`));
    } else {
      if (location.pathname.includes('/onboarding/links')) {
        localStorage.setItem('onboardingPath', '/onboarding/profile');
        yield put(SetRedirectControl('/onboarding/profile'));
      } else {
        localStorage.setItem('onboardingPath', '/onboarding/link');
        yield put(SetRedirectControl('/onboarding/link'));
      }
    }
  } catch (error) {
    handleError(error, { category: ErrorCategory.API });
    yield put(SetLoading(false));
  }
}

export function* DeleteLinkSaga({
  payload,
}: PayloadAction<string[]>): Generator {
  try {
    yield put(SetLoading(true));
    const { userID, isOnboardingPassed, username } = (yield select(
      (state: {
        User: { _id: string; onboardingPassed: boolean; username: string };
      }) => ({
        userID: state.User._id,
        isOnboardingPassed: !!state.User.onboardingPassed,
        username: state.User?.username,
      }),
    )) as {
      userID: string;
      isOnboardingPassed: boolean;
      username: string;
    };

    const { data } = (yield client().mutate({
      mutation: deleteLink,
      variables: { input: { _ids: payload } },
    })) as { data: { deleteLink: Link[] } };

    yield put(
      UpdatedMarbleUser({
        links: data?.deleteLink,
      }),
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      userID,
      event: 'linkDeleted',
    });

    if (isOnboardingPassed) {
      yield put(SetRedirectControl(`/${username}`));
    } else {
      if (location.pathname.includes('/onboarding/links')) {
        localStorage.setItem('onboardingPath', '/onboarding/links');
        yield put(SetRedirectControl(`/onboarding/links`));
      } else {
        localStorage.setItem('onboardingPath', '/onboarding/link');
        yield put(SetRedirectControl(`/onboarding/link`));
      }
    }

    yield put(SetLoading(false));
  } catch (error) {
    handleError(error, { category: ErrorCategory.API });
    yield put(SetLoading(false));
  }
}
